import { useBottomSheet } from '../../../../state-management/contexts/BottomSheetContext'
import { useNavigate } from 'react-router-dom'
import { isWithinDays } from '../../../../utils/utils'
import { openEliteLandingPage, trackEvent } from '../../../../utils/appBridge'
import { EventNames, type ElitePlanOfferType } from '../../../../types/misc.types'
import PrimaryButton from '../../../ui-components/PrimaryButton'
import NoRebalanceIcon from '../../../../icons/rebalance/NoRebalanceIcon'
import PlanBanner from './PlanBanner'
import { FamilyRiskProfilePromptSheet, RiskProfilePromptSheet } from '../RiskProfilePromptSheets'
import { EliteOfferBanner } from '../../../elite/landing-page/versions/landing-page-head/EliteOfferBanner'
import { useAppMetaContent } from '../../../../state-management/providers/AppMetaContentProvider'

const NoRebalanceNeeded = ({
  missedGains = 0,
  isUserElite,
  riskProfile,
  rebalanceSnapshotDate,
  planDurationInMonths,
  rebalancePlanCount,
  throughFamilyAccess,
  primaryUserName,
  secondaryUserName,
  consentText,
  secondaryUserPhoneNumber,
  specialOffering,
  signupOffer,
  offerStartTime,
  offerExpiryTime,
  offerType,
  membershipFees
}: {
  missedGains: number
  isUserElite: boolean
  riskProfile: string | null
  rebalanceSnapshotDate?: number
  planDurationInMonths: number
  rebalancePlanCount: number
  throughFamilyAccess?: boolean
  primaryUserName?: string
  secondaryUserName?: string
  consentText?: string
  secondaryUserPhoneNumber?: string
  specialOffering?: boolean
  signupOffer?: boolean
  offerStartTime?: number | null
  offerExpiryTime?: number | null
  offerType?: ElitePlanOfferType | null
  membershipFees?: number
}) => {
  const { openBottomSheet, closeBottomSheet, destroyBottomSheet } = useBottomSheet()
  const navigate = useNavigate()
  const shouldShowBanner = isWithinDays(rebalanceSnapshotDate, 30)
  const { getEliteMembersDisplay } = useAppMetaContent()
  const handleClick = () => {
    if (isUserElite && (!riskProfile || riskProfile === 'null')) {
      if (throughFamilyAccess && consentText) {
        openBottomSheet(
          <FamilyRiskProfilePromptSheet
            onClose={closeBottomSheet}
            onDestroy={destroyBottomSheet}
            onNavigate={navigate}
            familyMemberName={secondaryUserName ?? ''}
            consentText={consentText ?? ''}
            familyMemberPhoneNumber={secondaryUserPhoneNumber ?? ''}
          />
        )
      } else {
        openBottomSheet(<RiskProfilePromptSheet onClose={closeBottomSheet} onNavigate={navigate} />)
      }
    } else {
      isUserElite ? navigate('/rebalance-plan') : openEliteLandingPage()
    }
  }
  return (
    <div className='text-brown-dark'>
      {isUserElite ? (
        <h2 className='text-[22px] text-orange10 text-left'>Your Rebalancing Plan</h2>
      ) : (
        <h2
          className='text-[22px] text-orange10 text-left'
          aria-label="We analysed your portfolio and it's already in top shape!"
        >
          {/* aria-hidden: <strong> was a second TalkBack stop + re-announced "top shape" (Reg_Power_Rebalancing_B03) */}
          <span aria-hidden='true'>
            We analysed your portfolio and it's already in <span className='font-[700]'>top shape!</span>
          </span>
        </h2>
      )}
      <div className={`relative ${shouldShowBanner ? 'mt-[58px]' : ''}`}>
        <PlanBanner
          rebalanceSnapshotDate={rebalanceSnapshotDate}
          planDurationInMonths={planDurationInMonths}
          rebalancePlanCount={rebalancePlanCount}
        />
        <div className='bg-white rounded-2xl px-4 py-5 shadow-[2px_2px_0_0#CC5C2D] my-6 flex flex-row gap-4 z-10 relative'>
          <div className='bg-gray-50 w-[60px] rounded-xl'>
            <NoRebalanceIcon />
          </div>
          <div>
            <p className='text-base text-black font-medium text-left'>All your rated funds are already in top shape</p>
            {missedGains === 0 && <p className='text-13 text-grey40 text-left mt-2'>No missed gains detected.</p>}
          </div>
        </div>
      </div>
      {!isUserElite && (
        <>
          <div className='text-base text-orange10 text-left mb-8'>
            <span className='font-medium'>Always keep your portfolio in top form with Elite.</span> Get Rebalance Plan, Power Rank, Trends and a lot more.
          </div>
          <EliteOfferBanner
            specialOffering={specialOffering ?? false}
            signupOffer={signupOffer ?? false}
            offerStartTime={offerStartTime}
            offerExpiryTime={offerExpiryTime}
            offerType={offerType}
            membershipFees={membershipFees ?? 0}
            source='NoRebalanceNeeded'
            screenType='MF_Smart_Rebalancing'
          />
        </>
      )}
      {isUserElite ? (
        <div>
          <PrimaryButton
            onClick={() => {
              trackEvent({
                eventName: EventNames.UserClicked,
                payload: {
                  eventId: 'NoRebalanceNeededClicked',
                  buttonLabel: 'View details'
                }
              })
              handleClick()
            }}
            isFullWidth
            label='View details'
            aria-label='View details, double tap to view details'
            labelClassName='text-white'
          />
        </div>
      ) : (
        <div className='relative'>
          <PrimaryButton
            onClick={() => {
              trackEvent({
                eventName: EventNames.UserClicked,
                payload: {
                  eventId: 'NoRebalanceNeededClicked',
                  buttonLabel: 'Unlock Plan'
                }
              })
              handleClick()
            }}
            isFullWidth
            label='Unlock Plan'
            aria-label='Unlock Plan, double tap to unlock plan'
            labelClassName='text-white'
          />
        </div>
      )}
      <div className='text-13 text-orange10 mt-3 text-center'>Trusted by {getEliteMembersDisplay('short')} Elite members</div>
    </div>
  )
}

export default NoRebalanceNeeded
